import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useUserPlan } from '../hooks/useUserPlan';
import { defaultTemplates, NOTIFICATION_SECTIONS, AVAILABLE_VARIABLES } from '../utils/notificationTemplates';
import { Icon } from '@iconify/react';
import { showToast } from '../Toast';

export default function NotificationSettings() {
  const { isProOrAbove, isPremiumOrAbove } = useUserPlan();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configDays, setConfigDays] = useState({
    due_reminder: 5,
    recurring_overdue: 1
  });

  const [whatsappProfile, setWhatsappProfile] = useState({
    display_name: '',
    phone_number: '',
    avatar_url: ''
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [enabledNotifications, setEnabledNotifications] = useState({});

  useEffect(() => {
    const defaults = {};
    NOTIFICATION_SECTIONS.forEach(section => {
      section.items.forEach(item => {
        defaults[`email_${item.id}`] = item.defaultEmail;
        defaults[`whatsapp_${item.id}`] = item.defaultSms;
      });
    });
    setEnabledNotifications(defaults);
  }, []);

  // State for Edit Modal
  const [editingItem, setEditingItem] = useState(null); // { id, label }
  const [activeTab, setActiveTab] = useState('email'); // 'email' | 'whatsapp'
  const [modalOpen, setModalOpen] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchTemplates(), fetchSettings()]);
      setLoading(false);
    };
    loadAll();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('notification_settings')
        .select('config')
        .eq('user_id', user.id)
        .single();

      if (data?.config) {
        if (data.config.days_before_due) {
          setConfigDays(prev => ({ ...prev, due_reminder: data.config.days_before_due }));
        }
        if (data.config.days_after_due) {
          setConfigDays(prev => ({ ...prev, recurring_overdue: data.config.days_after_due }));
        }
        if (data.config.enabled_channels) {
          setEnabledNotifications(prev => ({ ...prev, ...data.config.enabled_channels }));
        }
        if (data.config.whatsapp_profile) {
          setWhatsappProfile(data.config.whatsapp_profile);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleWhatsappAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Selecione um arquivo de imagem (PNG, JPG, etc.)', 'warning');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast('A imagem deve ter no maximo 2MB', 'warning');
      return;
    }

    setUploadingAvatar(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const ext = file.name.split('.').pop();
      const fileName = `whatsapp-avatars/${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = await supabase
        .from('usuarios')
        .select('avatar_url')
        .eq('id', user.id)
        .maybeSingle();

      if (data?.avatar_url) {
        setWhatsappProfile(prev => ({ ...prev, avatar_url: data.avatar_url }));
      }
      showToast('Foto carregada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro no upload:', error);
      showToast('Erro ao fazer upload: ' + error.message, 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const saveWhatsappProfile = async () => {
    try {
        setSaving(true);
        const { data: { user } } = await supabase.auth.getUser();

        // Get current config to merge
        const { data: currentSettings } = await supabase
            .from('notification_settings')
            .select('config')
            .eq('user_id', user.id)
            .single();
        
        const currentConfig = currentSettings?.config || {};

        const newConfig = {
            ...currentConfig,
            whatsapp_profile: whatsappProfile
        };

        const { error } = await supabase
            .from('notification_settings')
            .upsert({
                user_id: user.id,
                config: newConfig,
                updated_at: new Date().toISOString()
            });

        if (error) throw error;
        showToast('Perfil do WhatsApp salvo com sucesso!', 'success');
    } catch (error) {
        console.error('Error saving whatsapp profile:', error);
        showToast('Erro ao salvar perfil do WhatsApp', 'error');
    } finally {
        setSaving(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      // Convert array to object for easier access
      const templateMap = { email: {}, whatsapp: {} };
      data.forEach(t => {
        if (!templateMap[t.type]) templateMap[t.type] = {};
        templateMap[t.type][t.category] = t;
      });

      setTemplates(templateMap);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const saveSettings = async (updates) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const newConfig = {
        days_before_due: updates.days_before_due ?? configDays.due_reminder,
        days_after_due: updates.days_after_due ?? configDays.recurring_overdue,
        enabled_channels: updates.enabled_channels ?? enabledNotifications,
        whatsapp_profile: whatsappProfile
      };

      const { error } = await supabase
        .from('notification_settings')
        .upsert({
          user_id: user.id,
          config: newConfig,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Erro ao salvar configurações', 'error');
    }
  };

  const isCustomized = (type, category) => {
    return !!templates[type]?.[category];
  };

  const getStatusText = (type, category) => {
    const template = templates[type]?.[category];
    if (template) {
      if (type === 'whatsapp') {
        if (template.status === 'pending') {
          return <span style={{ color: '#ff9800', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><Icon icon="mdi:clock-outline" /> Em análise</span>;
        }
        if (template.status === 'rejected') {
          return <span style={{ color: '#d32f2f', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><Icon icon="mdi:alert-circle-outline" /> Rejeitado</span>;
        }
      }
      return <span style={{ color: 'var(--primary-color)', fontWeight: '500' }}>Personalizado</span>;
    }
    return <span style={{ color: '#999' }}>Notificação no padrão Clickpag</span>;
  };

  const handleToggle = (type, category) => {
    const key = `${type}_${category}`;
    const newState = !enabledNotifications[key];
    
    setEnabledNotifications(prev => ({
      ...prev,
      [key]: newState
    }));

    saveSettings({
      enabled_channels: {
        ...enabledNotifications,
        [key]: newState
      }
    });

    showToast(`${type === 'email' ? 'Email' : 'WhatsApp'} para "${category}" ${newState ? 'ativado' : 'desativado'}`, 'success');
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setActiveTab('email'); // Default to email
    loadTemplateData(item.id, 'email');
    setModalOpen(true);
  };

  const loadTemplateData = (category, type) => {
    const dbTemplate = templates[type]?.[category];
    const defaultTemplate = defaultTemplates[type]?.[category];
    
    let subject = '';
    let content = '';

    if (type === 'email') {
      subject = dbTemplate?.subject || defaultTemplate?.subject || '';
      content = dbTemplate?.content || defaultTemplate?.content || '';
    } else {
      content = dbTemplate?.content || defaultTemplate || '';
    }

    setEditSubject(subject);
    setEditContent(content);
  };

  useEffect(() => {
    if (editingItem && modalOpen) {
      loadTemplateData(editingItem.id, activeTab);
    }
  }, [activeTab]);

  const handleSaveTemplate = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();

      const payload = {
        user_id: user.id,
        type: activeTab,
        category: editingItem.id,
        content: editContent,
        active: true,
        status: activeTab === 'whatsapp' ? 'pending' : 'approved'
      };

      if (activeTab === 'email') {
        payload.subject = editSubject;
      }

      const { error } = await supabase
        .from('notification_templates')
        .upsert(payload, { onConflict: 'user_id,type,category' });

      if (error) throw error;

      showToast(
        activeTab === 'whatsapp' 
          ? 'Solicitação enviada para análise!' 
          : 'Notificação salva com sucesso!', 
        'success'
      );
      fetchTemplates();
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving:', error);
      showToast('Erro ao salvar notificação', 'error');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Variável copiada!', 'success');
  };

  const canEdit = (type) => {
    if (type === 'email') return isProOrAbove;
    if (type === 'whatsapp') return isPremiumOrAbove;
    return false;
  };

  return (
    <div className="notification-settings" style={{ paddingBottom: '40px' }}>
      
      {/* WhatsApp Profile Section - Premium Only */}
      {isPremiumOrAbove && (
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon icon="mdi:whatsapp" color="#25D366" />
            Perfil WhatsApp Personalizado
            {whatsappProfile.phone_number && (
              <span style={{ 
                fontSize: '12px', 
                background: '#e8f5e9', 
                color: '#2e7d32', 
                padding: '2px 8px', 
                borderRadius: '12px', 
                border: '1px solid #c8e6c9',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Icon icon="mdi:check-decagram" /> Ativo
              </span>
            )}
          </h3>
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            border: '1px solid #e0e0e0',
            padding: '24px',
            display: 'flex',
            gap: '32px',
            alignItems: 'flex-start'
          }}>
            {/* Avatar Upload */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '100px', 
                height: '100px', 
                borderRadius: '50%', 
                background: '#f5f5f5', 
                marginBottom: '12px',
                backgroundImage: `url(${whatsappProfile.avatar_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #eee'
              }}>
                {!whatsappProfile.avatar_url && <Icon icon="mdi:camera" width="32" color="#ccc" />}
              </div>
              <label style={{ 
                cursor: 'pointer', 
                color: 'var(--primary-color)', 
                fontSize: '13px', 
                fontWeight: '500',
                display: 'block' 
              }}>
                {uploadingAvatar ? 'Enviando...' : 'Alterar foto'}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleWhatsappAvatarUpload} 
                  style={{ display: 'none' }} 
                  disabled={uploadingAvatar}
                />
              </label>
            </div>

            {/* Fields */}
            <div style={{ flex: 1, maxWidth: '500px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#666' }}>
                  Nome de Exibição
                </label>
                <input 
                  type="text" 
                  value={whatsappProfile.display_name}
                  onChange={(e) => setWhatsappProfile(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="Ex: Minha Empresa"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: '6px', 
                    border: '1px solid #ddd',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#666' }}>
                  Número do WhatsApp
                </label>
                <input 
                  type="text" 
                  value={whatsappProfile.phone_number}
                  onChange={(e) => setWhatsappProfile(prev => ({ ...prev, phone_number: e.target.value }))}
                  placeholder="Ex: 5511999999999"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: '6px', 
                    border: '1px solid #ddd',
                    fontSize: '14px'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  * O número deve incluir o código do país (55) e DDD.
                </p>
              </div>
              <button 
                onClick={saveWhatsappProfile}
                disabled={saving}
                style={{ 
                  background: '#25D366', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 20px', 
                  borderRadius: '6px', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Salvando...' : 'Salvar Perfil WhatsApp'}
              </button>
            </div>
            
            {/* Info Box */}
            <div style={{ 
                flex: 1, 
                background: '#f0f9ff', 
                border: '1px solid #b3e5fc', 
                borderRadius: '8px', 
                padding: '16px',
                fontSize: '13px',
                color: '#0277bd'
            }}>
                <strong><Icon icon="mdi:information-outline" style={{ verticalAlign: 'middle', marginRight: '4px' }}/> Sobre o número personalizado</strong>
                <p style={{ margin: '8px 0 0 0', lineHeight: '1.5' }}>
                    Como assinante Premium, você pode utilizar seu próprio número e foto de perfil para envio das notificações. 
                    Certifique-se de que o número está ativo e verificado.
                </p>
            </div>
          </div>
        </div>
      )}

      {NOTIFICATION_SECTIONS.map((section, idx) => (
        <div key={idx} style={{ marginBottom: '40px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: 'var(--text-primary)' }}>
            {section.title}
          </h3>

          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            border: '1px solid #e0e0e0',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '2fr 100px 100px 140px', 
              padding: '15px 20px', 
              background: '#f9fafb',
              borderBottom: '1px solid #e0e0e0',
              fontWeight: '600',
              fontSize: '13px',
              color: '#666'
            }}>
              <div>Tipo de notificação</div>
              <div style={{ textAlign: 'center' }}>E-mail</div>
              <div style={{ textAlign: 'center' }}>WhatsApp</div>
              <div style={{ textAlign: 'right' }}>Ações</div>
            </div>

            {/* Items */}
            {section.items.map((item, itemIdx) => (
              <div key={item.id} style={{ 
                display: 'grid', 
                gridTemplateColumns: '2fr 100px 100px 140px', 
                padding: '20px', 
                borderBottom: itemIdx < section.items.length - 1 ? '1px solid #f0f0f0' : 'none',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: '12px', marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                       <Icon icon="mdi:email-outline" width="14" color="#666" />
                       {getStatusText('email', item.id)}
                     </div>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                       <Icon icon="mdi:whatsapp" width="14" color="#666" />
                       {getStatusText('whatsapp', item.id)}
                     </div>
                  </div>

                  {item.id === 'due_reminder' && (
                    <div style={{ fontSize: '13px', color: '#666', display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                      Enviar 
                      <select 
                        value={configDays.due_reminder} 
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setConfigDays({...configDays, due_reminder: val});
                          saveSettings({ days_before_due: val });
                        }}
                        style={{ margin: '0 8px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd', background: 'white' }}
                      >
                        {[5, 10, 15, 30].map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      dias antes do vencimento
                    </div>
                  )}

                  {item.id === 'recurring_overdue' && (
                    <div style={{ fontSize: '13px', color: '#666', display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                      Enviar a cada
                      <select 
                        value={configDays.recurring_overdue} 
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setConfigDays({...configDays, recurring_overdue: val});
                          saveSettings({ days_after_due: val });
                        }}
                        style={{ margin: '0 8px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd', background: 'white' }}
                      >
                        {[1, 3, 7, 15, 30].map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      dias após o vencimento
                    </div>
                  )}
                </div>

                {/* Email Toggle */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                   {item.defaultEmail && (
                     <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '40px', height: '22px' }}>
                       <input 
                         type="checkbox" 
                         checked={!!enabledNotifications[`email_${item.id}`]}
                         onChange={() => handleToggle('email', item.id)}
                         style={{ opacity: 0, width: 0, height: 0 }} 
                       />
                       <span style={{ 
                         position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, 
                         backgroundColor: enabledNotifications[`email_${item.id}`] ? 'var(--primary-color)' : '#ccc', 
                         borderRadius: '34px', transition: '.4s' 
                       }}></span>
                       <span style={{ 
                         position: 'absolute', content: '""', height: '16px', width: '16px', left: '3px', bottom: '3px', 
                         backgroundColor: 'white', borderRadius: '50%', transition: '.4s', 
                         transform: enabledNotifications[`email_${item.id}`] ? 'translateX(18px)' : 'translateX(0)' 
                       }}></span>
                     </label>
                   )}
                </div>

                {/* WhatsApp Toggle */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                   {item.defaultSms && (
                     <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '40px', height: '22px' }}>
                       <input 
                         type="checkbox" 
                         checked={!!enabledNotifications[`whatsapp_${item.id}`]}
                         onChange={() => handleToggle('whatsapp', item.id)}
                         style={{ opacity: 0, width: 0, height: 0 }} 
                       />
                       <span style={{ 
                         position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, 
                         backgroundColor: enabledNotifications[`whatsapp_${item.id}`] ? '#25D366' : '#ccc', 
                         borderRadius: '34px', transition: '.4s' 
                       }}></span>
                       <span style={{ 
                         position: 'absolute', content: '""', height: '16px', width: '16px', left: '3px', bottom: '3px', 
                         backgroundColor: 'white', borderRadius: '50%', transition: '.4s', 
                         transform: enabledNotifications[`whatsapp_${item.id}`] ? 'translateX(18px)' : 'translateX(0)' 
                       }}></span>
                     </label>
                   )}
                </div>

                {/* Actions */}
                <div style={{ textAlign: 'right' }}>
                  <button 
                    onClick={() => openEditModal(item)}
                    style={{ 
                      background: 'transparent', 
                      border: '1px solid #e0e0e0', 
                      padding: '6px 12px', 
                      borderRadius: '6px', 
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <Icon icon="mdi:pencil-outline" width="16" />
                    Editar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      {modalOpen && editingItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            background: 'white',
            width: '900px',
            maxWidth: '95vw',
            height: '85vh',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            {/* Modal Header */}
            <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px' }}>Editar notificação</h3>
                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                  {editingItem.label}
                </p>
              </div>
              <button onClick={() => setModalOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <Icon icon="mdi:close" width="24" color="#666" />
              </button>
            </div>
            
            {/* Tabs */}
            <div style={{ padding: '0 20px', borderBottom: '1px solid #eee', display: 'flex', gap: '20px' }}>
               <button 
                 onClick={() => setActiveTab('email')}
                 style={{ 
                   padding: '12px 0', 
                   background: 'transparent', 
                   border: 'none', 
                   borderBottom: activeTab === 'email' ? '2px solid var(--primary-color)' : '2px solid transparent',
                   color: activeTab === 'email' ? 'var(--primary-color)' : '#666',
                   fontWeight: activeTab === 'email' ? '600' : '400',
                   cursor: 'pointer',
                   display: 'flex',
                   alignItems: 'center',
                   gap: '6px'
                 }}
               >
                 <Icon icon="mdi:email-outline" /> E-mail
               </button>
               <button 
                 onClick={() => setActiveTab('whatsapp')}
                 style={{ 
                   padding: '12px 0', 
                   background: 'transparent', 
                   border: 'none', 
                   borderBottom: activeTab === 'whatsapp' ? '2px solid #25D366' : '2px solid transparent',
                   color: activeTab === 'whatsapp' ? '#25D366' : '#666',
                   fontWeight: activeTab === 'whatsapp' ? '600' : '400',
                   cursor: 'pointer',
                   display: 'flex',
                   alignItems: 'center',
                   gap: '6px'
                 }}
               >
                 <Icon icon="mdi:whatsapp" /> WhatsApp
               </button>
            </div>

            {/* Modal Content */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* Editor Column */}
              <div style={{ flex: 2, padding: '24px', overflowY: 'auto', borderRight: '1px solid #eee' }}>
                
                {!canEdit(activeTab) && (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '12px 16px', 
                    background: '#e3f2fd', 
                    border: '1px solid #bbdefb', 
                    borderRadius: '8px', 
                    color: '#0d47a1',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <Icon icon="mdi:eye" width="20" />
                    <div>
                      Modo de visualização. Este é o <strong>modelo padrão</strong> enviado aos clientes.
                      {!canEdit(activeTab) && activeTab === 'whatsapp' && ' Para personalizar, é necessário o plano Premium.'}
                      {!canEdit(activeTab) && activeTab === 'email' && ' Para personalizar, é necessário o plano Pro.'}
                    </div>
                  </div>
                )}

                {activeTab === 'email' && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Assunto do e-mail</label>
                    <input 
                      type="text" 
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      disabled={!canEdit(activeTab)}
                      style={{ 
                        width: '100%', 
                        padding: '12px', 
                        borderRadius: '8px', 
                        border: '1px solid #ddd',
                        fontSize: '14px'
                      }} 
                    />
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Mensagem</label>
                  <textarea 
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    disabled={!canEdit(activeTab)}
                    style={{ 
                      width: '100%', 
                      height: '350px', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      lineHeight: '1.5',
                      resize: 'none'
                    }} 
                  />
                </div>
              </div>

              {/* Variables Column */}
              <div style={{ flex: 1, padding: '24px', overflowY: 'auto', background: '#f9fafb' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>Lista de variáveis</h4>
                
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '20px', lineHeight: '1.5' }}>
                  As variáveis são textos dinâmicos que mudam de acordo com as informações. 
                  Clique para copiar.
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {AVAILABLE_VARIABLES.map(v => (
                    <div key={v.code} style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <code style={{ background: '#eee', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', color: '#d32f2f' }}>{v.code}</code>
                        <button 
                          onClick={() => copyToClipboard(v.code)}
                          style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            cursor: 'pointer', 
                            color: 'var(--primary-color)',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          Copiar
                        </button>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{v.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                onClick={() => setModalOpen(false)}
                style={{ 
                  padding: '10px 20px', 
                  borderRadius: '6px', 
                  border: '1px solid #ddd', 
                  background: 'white', 
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Fechar
              </button>
              
              {canEdit(activeTab) ? (
                <button 
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: '6px', 
                    border: 'none', 
                    background: activeTab === 'whatsapp' ? '#25D366' : 'var(--primary-color)', 
                    color: 'white', 
                    cursor: saving ? 'wait' : 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {saving && <Icon icon="eos-icons:loading" />}
                  {activeTab === 'whatsapp' ? 'Solicitar Aprovação' : 'Salvar Alteração'}
                </button>
              ) : (
                <button 
                  onClick={() => window.location.href = '/app/configuracao?aba=upgrade'}
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: '6px', 
                    border: 'none', 
                    background: '#ff9800', 
                    color: 'white', 
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icon icon="mdi:rocket-launch" />
                  Fazer Upgrade
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
